import { useState, useEffect } from 'react';
import { RefreshCw, Play, Pause, Activity, AlertCircle, Search, X, Zap, GitBranch } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

const glass = {
  background: 'linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255,255,255,0.09)',
  borderTop: '1px solid rgba(255,255,255,0.16)',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.07)',
};

export default function WorkflowManager() {
  const [workflows, setWorkflows] = useState([]);
  const [filteredWorkflows, setFilteredWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });
  const [actionInProgress, setActionInProgress] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'active' | 'inactive'

  const fetchWorkflows = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/workflows`);
      if (!response.ok) throw new Error(`Failed to fetch workflows (${response.status})`);
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Workflow API not reachable. On local dev, start the backend: cd backend && node server.js');
      }
      const raw = await response.json();
      const list = Array.isArray(raw) ? raw : raw.data || [];
      setWorkflows(list);
      updateStats(list);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateStats = (list) => {
    const active = list.filter(w => w.active).length;
    setStats({ total: list.length, active, inactive: list.length - active });
  };

  const applyFilters = (list, q, f) => {
    let out = list;
    if (q.trim()) out = out.filter(w => w.name.toLowerCase().includes(q.toLowerCase()) || w.id.toLowerCase().includes(q.toLowerCase()));
    if (f === 'active') out = out.filter(w => w.active);
    if (f === 'inactive') out = out.filter(w => !w.active);
    setFilteredWorkflows(out);
  };

  const handleSearch = (value) => {
    setSearch(value);
    applyFilters(workflows, value, filter);
  };

  const handleFilter = (f) => {
    setFilter(f);
    applyFilters(workflows, search, f);
  };

  useEffect(() => {
    applyFilters(workflows, search, filter);
  }, [workflows]);

  const toggleWorkflow = async (workflowId, action) => {
    setActionInProgress(workflowId);
    try {
      const response = await fetch(`${API_URL}/api/workflows?id=${workflowId}&action=${action}`, { method: 'POST' });
      if (!response.ok) throw new Error(`Failed to ${action} workflow`);
      await fetchWorkflows();
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setActionInProgress(null);
    }
  };

  const bulkToggle = async (action) => {
    setActionInProgress('bulk');
    try {
      const toToggle = workflows.filter(w => action === 'activate' ? !w.active : w.active);
      for (const wf of toToggle) {
        await fetch(`${API_URL}/api/workflows?id=${wf.id}&action=${action}`, { method: 'POST' });
      }
      await fetchWorkflows();
    } catch (err) {
      setError(`Bulk ${action} failed`);
    } finally {
      setActionInProgress(null);
    }
  };

  useEffect(() => {
    fetchWorkflows();
    const interval = setInterval(fetchWorkflows, 30000);
    return () => clearInterval(interval);
  }, []);

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) : '—';

  return (
    <div style={{ background: '#0a0b0d', minHeight: '100vh', fontFamily: "'Space Grotesk', sans-serif", color: '#fff', paddingTop: '80px', paddingBottom: '48px' }}>
      <style>{`
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes fadeIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:none} }
        .wf-row:hover { background: rgba(255,255,255,0.03) !important; }
        .wf-row { transition: background 0.15s; }
        .wf-toggle:hover { opacity: 0.85; transform: scale(1.03); }
        .wf-toggle { transition: opacity 0.15s, transform 0.15s; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 4px; }
      `}</style>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>

        {/* ── HEADER ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '42px', height: '42px', borderRadius: '12px', background: 'linear-gradient(135deg, rgba(56,254,96,0.2), rgba(56,254,96,0.06))', border: '1px solid rgba(56,254,96,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <GitBranch size={20} color="#38FE60" />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#38FE60', fontSize: '10px', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase' }}>n8n</span>
              </div>
              <h1 style={{ fontSize: '20px', fontWeight: 800, letterSpacing: '-0.03em', margin: 0, lineHeight: 1.2 }}>Workflow Management</h1>
            </div>
          </div>

          {/* Stat pills + refresh */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {[
              { label: 'Total', val: stats.total, color: '#60a5fa' },
              { label: 'Active', val: stats.active, color: '#38FE60' },
              { label: 'Inactive', val: stats.inactive, color: '#ff4d4d' },
            ].map(s => (
              <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', padding: '4px 12px' }}>
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: s.color, boxShadow: `0 0 6px ${s.color}` }} />
                <span style={{ color: '#aaa', fontSize: '11px' }}>{s.label}</span>
                <span style={{ color: s.color, fontSize: '13px', fontWeight: 700 }}>{s.val}</span>
              </div>
            ))}
            <button
              onClick={fetchWorkflows}
              disabled={loading}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', color: '#ccc', fontSize: '12px', fontWeight: 600, padding: '7px 14px', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1, fontFamily: 'inherit' }}
            >
              <RefreshCw size={13} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
              Refresh
            </button>
          </div>
        </div>

        {/* ── ERROR BANNER ── */}
        {error && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,77,77,0.08)', border: '1px solid rgba(255,77,77,0.2)', borderRadius: '12px', padding: '12px 16px', marginBottom: '16px', animation: 'fadeIn 0.2s ease' }}>
            <AlertCircle size={15} color="#ff4d4d" style={{ flexShrink: 0 }} />
            <span style={{ color: '#ff8080', fontSize: '13px', flex: 1 }}>{error}</span>
            <button onClick={() => setError('')} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', padding: '2px', display: 'flex' }}>
              <X size={14} />
            </button>
          </div>
        )}

        {/* ── TOOLBAR ── */}
        <div style={{ ...glass, padding: '14px 16px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{ flex: '1 1 220px', display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '7px 12px', minWidth: '160px' }}>
            <Search size={13} color="#555" style={{ flexShrink: 0 }} />
            <input
              type="text"
              placeholder="Search by name or ID…"
              value={search}
              onChange={e => handleSearch(e.target.value)}
              style={{ background: 'none', border: 'none', outline: 'none', color: '#ddd', fontSize: '12px', width: '100%', fontFamily: 'inherit' }}
            />
            {search && <button onClick={() => handleSearch('')} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', padding: 0, display: 'flex', flexShrink: 0 }}><X size={12} /></button>}
          </div>

          {/* Filter tabs */}
          <div style={{ display: 'flex', gap: '4px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', padding: '3px' }}>
            {[['all', 'All', '#aaa'], ['active', 'Active', '#38FE60'], ['inactive', 'Inactive', '#ff4d4d']].map(([f, label, col]) => (
              <button
                key={f}
                onClick={() => handleFilter(f)}
                style={{ background: filter === f ? 'rgba(255,255,255,0.08)' : 'none', border: 'none', borderRadius: '7px', color: filter === f ? col : '#555', fontSize: '11px', fontWeight: 700, padding: '5px 12px', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Bulk actions */}
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              onClick={() => bulkToggle('activate')}
              disabled={!!actionInProgress}
              style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'rgba(56,254,96,0.08)', border: '1px solid rgba(56,254,96,0.2)', borderRadius: '9px', color: '#38FE60', fontSize: '11px', fontWeight: 700, padding: '6px 12px', cursor: actionInProgress ? 'not-allowed' : 'pointer', opacity: actionInProgress ? 0.5 : 1, fontFamily: 'inherit' }}
            >
              <Play size={11} /> Activate All
            </button>
            <button
              onClick={() => bulkToggle('deactivate')}
              disabled={!!actionInProgress}
              style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'rgba(255,77,77,0.08)', border: '1px solid rgba(255,77,77,0.2)', borderRadius: '9px', color: '#ff4d4d', fontSize: '11px', fontWeight: 700, padding: '6px 12px', cursor: actionInProgress ? 'not-allowed' : 'pointer', opacity: actionInProgress ? 0.5 : 1, fontFamily: 'inherit' }}
            >
              <Pause size={11} /> Deactivate All
            </button>
          </div>
        </div>

        {/* ── TABLE ── */}
        <div style={{ ...glass, overflow: 'hidden' }}>
          {/* Table header */}
          <div style={{ display: 'grid', gridTemplateColumns: '20px 1fr 160px 90px 100px', gap: '0 16px', padding: '10px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', alignItems: 'center' }}>
            {['', 'Workflow Name', 'ID', 'Created', 'Action'].map((h, i) => (
              <span key={i} style={{ color: '#444', fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', textAlign: i >= 3 ? 'center' : 'left' }}>{h}</span>
            ))}
          </div>

          {/* Loading */}
          {loading && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 0', gap: '12px' }}>
              <RefreshCw size={28} color="#38FE60" style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ color: '#555', fontSize: '13px' }}>Fetching workflows…</span>
            </div>
          )}

          {/* Empty */}
          {!loading && filteredWorkflows.length === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 0', gap: '10px' }}>
              <Activity size={36} color="#222" />
              <span style={{ color: '#444', fontSize: '13px', fontWeight: 600 }}>{search ? 'No workflows match your search' : 'No workflows found'}</span>
              <span style={{ color: '#333', fontSize: '11px' }}>{search ? 'Try a different term' : 'Your workflows will appear here'}</span>
            </div>
          )}

          {/* Rows */}
          {!loading && filteredWorkflows.map((wf, i) => {
            const isLast = i === filteredWorkflows.length - 1;
            const busy = actionInProgress === wf.id;
            return (
              <div
                key={wf.id}
                className="wf-row"
                style={{ display: 'grid', gridTemplateColumns: '20px 1fr 160px 90px 100px', gap: '0 16px', padding: '11px 20px', borderBottom: isLast ? 'none' : '1px solid rgba(255,255,255,0.04)', alignItems: 'center', animation: 'fadeIn 0.2s ease' }}
              >
                {/* Status dot */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: wf.active ? '#38FE60' : '#333', boxShadow: wf.active ? '0 0 8px #38FE60' : 'none', flexShrink: 0 }} />
                </div>

                {/* Name */}
                <div style={{ minWidth: 0 }}>
                  <span style={{ color: '#e8e8e8', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{wf.name}</span>
                </div>

                {/* ID */}
                <div style={{ minWidth: 0 }}>
                  <code style={{ color: '#38FE60', fontSize: '10px', background: 'rgba(56,254,96,0.06)', border: '1px solid rgba(56,254,96,0.12)', borderRadius: '6px', padding: '2px 7px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', maxWidth: '150px' }}>{wf.id}</code>
                </div>

                {/* Created */}
                <span style={{ color: '#555', fontSize: '11px', textAlign: 'center' }}>{fmt(wf.createdAt)}</span>

                {/* Toggle button */}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <button
                    className="wf-toggle"
                    onClick={() => toggleWorkflow(wf.id, wf.active ? 'deactivate' : 'activate')}
                    disabled={busy || actionInProgress === 'bulk'}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '5px',
                      background: wf.active ? 'rgba(255,77,77,0.1)' : 'rgba(56,254,96,0.1)',
                      border: `1px solid ${wf.active ? 'rgba(255,77,77,0.25)' : 'rgba(56,254,96,0.25)'}`,
                      borderRadius: '8px', color: wf.active ? '#ff4d4d' : '#38FE60',
                      fontSize: '11px', fontWeight: 700, padding: '5px 12px',
                      cursor: (busy || actionInProgress === 'bulk') ? 'not-allowed' : 'pointer',
                      opacity: (busy || actionInProgress === 'bulk') ? 0.5 : 1,
                      fontFamily: 'inherit', whiteSpace: 'nowrap',
                    }}
                  >
                    {busy
                      ? <RefreshCw size={10} style={{ animation: 'spin 1s linear infinite' }} />
                      : wf.active ? <Pause size={10} /> : <Play size={10} />
                    }
                    {busy ? '…' : wf.active ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer count */}
        {!loading && filteredWorkflows.length > 0 && (
          <div style={{ textAlign: 'center', marginTop: '12px' }}>
            <span style={{ color: '#333', fontSize: '11px' }}>
              Showing {filteredWorkflows.length} of {workflows.length} workflow{workflows.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
